// maths.portll.net — active-child profile context.
//
// The descendant of antidote-dev's "view-as": switching the active child IS just
// switching profiles — no privilege machinery, because the trust boundary is the
// parent account. Active child is CLIENT state (localStorage); safe because every
// Firestore rule re-proves ownership via request.auth.uid, so a tampered client can
// only ever reach children of its OWN parent account.

import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { collection, onSnapshot, query, orderBy } from "firebase/firestore";
import { db, callVerifyChildPin, callVerifyParentPin } from "../lib/firebase.js";
import { useAuth } from "./AuthContext.jsx";

const ProfileContext = createContext(null);
const ACTIVE_KEY = "maths.activeChildId";

// The child's real name is a LOCAL-ONLY, freely-editable override that never leaves the
// device — the cloud stores only a non-PII `username`. Keyed by childId in localStorage.
const NAME_PREFIX = "maths.childName.";
const readLocalName = (childId) => localStorage.getItem(NAME_PREFIX + childId) || "";
const writeLocalName = (childId, name) => {
  const v = String(name || "").trim();
  if (v) localStorage.setItem(NAME_PREFIX + childId, v);
  else localStorage.removeItem(NAME_PREFIX + childId);
};

export function ProfileProvider({ children }) {
  const { user } = useAuth();
  const [profiles, setProfiles] = useState([]);
  const [activeId, setActiveId] = useState(() => localStorage.getItem(ACTIVE_KEY) || null);
  const [loading, setLoading] = useState(true);
  const [localNames, setLocalNames] = useState({}); // childId → device-only real name

  useEffect(() => {
    if (!user) {
      setProfiles([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const q = query(collection(db, `users/${user.uid}/children`), orderBy("createdAt", "asc"));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setProfiles(rows);
        const names = {};
        for (const r of rows) { const n = readLocalName(r.id); if (n) names[r.id] = n; }
        setLocalNames(names);
        setLoading(false);
      },
      () => setLoading(false)
    );
    return unsub;
  }, [user]);

  function setActive(childId) {
    setActiveId(childId);
    if (childId) localStorage.setItem(ACTIVE_KEY, childId);
    else localStorage.removeItem(ACTIVE_KEY);
  }

  /**
   * Switch to a child profile. If the child has a PIN, `pin` must verify server-side.
   * Returns { ok, locked?, lockedUntil?, attemptsLeft? }.
   */
  async function switchProfile(childId, pin) {
    const child = profiles.find((p) => p.id === childId);
    if (child?.hasPin) {
      const res = await callVerifyChildPin({ childId, pin });
      if (!res.data.ok) return res.data;
    }
    setActive(childId);
    return { ok: true };
  }

  /** Grown-up gate: verify the parent PIN before parent mode. */
  async function verifyParentGate(pin) {
    try {
      const res = await callVerifyParentPin({ pin });
      return res.data;
    } catch (e) {
      // Cloud Functions not deployed yet (pre-Blaze): a parent PIN can only be SET via a
      // function too, so none can exist — the gate has nothing to verify. Degrade to open
      // rather than trap the parent. (This is a child-safety/UX gate, never a security
      // boundary; once functions ship, a real PIN verifies normally.)
      console.warn("verifyParentGate: functions unavailable, opening gate:", e?.code || e?.message);
      return { ok: true, noPin: true, degraded: true };
    }
  }

  /** Set/clear the device-only real name for a child (never synced to the cloud). */
  function setChildLocalName(childId, name) {
    writeLocalName(childId, name);
    setLocalNames((prev) => {
      const next = { ...prev };
      const v = String(name || "").trim();
      if (v) next[childId] = v; else delete next[childId];
      return next;
    });
  }

  /** What to SHOW for a child: the local real name if set, else the cloud username. */
  const displayNameFor = (child) => (child ? localNames[child.id] || child.username || "" : "");

  const activeChild = useMemo(() => profiles.find((p) => p.id === activeId) || null, [profiles, activeId]);

  const value = useMemo(
    () => ({ profiles, activeChild, activeId, loading, localNames, setActive, switchProfile, verifyParentGate, setChildLocalName, displayNameFor }),
    [profiles, activeChild, activeId, loading, localNames]
  );
  return <ProfileContext.Provider value={value}>{children}</ProfileContext.Provider>;
}

export function useProfiles() {
  const ctx = useContext(ProfileContext);
  if (!ctx) throw new Error("useProfiles must be used within ProfileProvider");
  return ctx;
}
